import { useEffect } from 'react';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from '../../data/seo';
type SeoProps = {
  title: string;
  description: string;
  path: string;
  keywords?: string[];
  image?: string;
  /** JSON-LD graph nodes rendered into a single script tag. */
  schemas?: Record<string, unknown>[];
  /** Keeps utility pages (404, thank-you) out of the index. */
  noindex?: boolean;
};
const MANAGED = 'data-seo-managed';
function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(MANAGED, 'true');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([key, value]) => el?.setAttribute(key, value));
}
function upsertLink(rel: string, href: string, extra: Record<string, string> = {}) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][${MANAGED}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([key, value]) => el?.setAttribute(key, value));
}

/**
 * Head manager: title, description, canonical, robots, Open Graph, Twitter
 * cards, and JSON-LD structured data for the current page.
 */
export function Seo({
  title,
  description,
  path,
  keywords = [],
  image = DEFAULT_OG_IMAGE,
  schemas = [],
  noindex = false
}: SeoProps) {
  useEffect(() => {
    const url = `${SITE_URL}${path === '/' ? '' : path}`;
    document.title = title;
    document.documentElement.lang = 'en-CA';
    upsertMeta(`meta[name="description"]`, {
      name: 'description',
      content: description
    });
    if (keywords.length > 0) {
      upsertMeta(`meta[name="keywords"]`, {
        name: 'keywords',
        content: keywords.join(', ')
      });
    }
    upsertMeta(`meta[name="robots"]`, {
      name: 'robots',
      content: noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1'
    });
    upsertMeta(`meta[name="author"]`, {
      name: 'author',
      content: SITE_NAME
    });
    upsertMeta(`meta[name="theme-color"]`, {
      name: 'theme-color',
      content: '#FAF8F5'
    });
    upsertMeta(`meta[name="viewport"]`, {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, viewport-fit=cover'
    });

    // Open Graph
    upsertMeta(`meta[property="og:title"]`, {
      property: 'og:title',
      content: title
    });
    upsertMeta(`meta[property="og:description"]`, {
      property: 'og:description',
      content: description
    });
    upsertMeta(`meta[property="og:type"]`, {
      property: 'og:type',
      content: 'website'
    });
    upsertMeta(`meta[property="og:url"]`, {
      property: 'og:url',
      content: url
    });
    upsertMeta(`meta[property="og:image"]`, {
      property: 'og:image',
      content: image
    });
    upsertMeta(`meta[property="og:image:alt"]`, {
      property: 'og:image:alt',
      content: `${SITE_NAME} — ${description.slice(0, 90)}`
    });
    upsertMeta(`meta[property="og:site_name"]`, {
      property: 'og:site_name',
      content: SITE_NAME
    });
    upsertMeta(`meta[property="og:locale"]`, {
      property: 'og:locale',
      content: 'en_CA'
    });

    // Twitter
    upsertMeta(`meta[name="twitter:card"]`, {
      name: 'twitter:card',
      content: 'summary_large_image'
    });
    upsertMeta(`meta[name="twitter:title"]`, {
      name: 'twitter:title',
      content: title
    });
    upsertMeta(`meta[name="twitter:description"]`, {
      name: 'twitter:description',
      content: description
    });
    upsertMeta(`meta[name="twitter:image"]`, {
      name: 'twitter:image',
      content: image
    });
    upsertLink('canonical', url);

    // Font performance
    upsertLink('preconnect', 'https://fonts.gstatic.com', {
      crossorigin: ''
    });

    // Structured data
    const existing = document.head.querySelector('script[data-seo-jsonld]');
    if (existing) existing.remove();
    if (schemas.length > 0) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-jsonld', 'true');
      script.textContent = JSON.stringify(schemas.length === 1 ? schemas[0] : schemas);
      document.head.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, noindex, JSON.stringify(keywords), JSON.stringify(schemas)]);
  return null;
}